import { createContext, useContext, useState } from 'react';
import { Dialog } from './ui/Dialog';

const ModalProviderContext = createContext<{
  open: boolean;
  modalContent: React.ReactNode;
  openModal: (content: React.ReactNode) => void;
  closeModal: () => void;
}>({
  open: false,
  modalContent: null,
  openModal: () => {},
  closeModal: () => {},
});

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);

  const openModal = (content: React.ReactNode) => {
    setModalContent(content);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setModalContent(null);
  };

  return (
    <ModalProviderContext.Provider
      value={{
        open,
        modalContent,
        openModal,
        closeModal,
      }}
    >
      {children}

      {open && (
        <Modal isOpen={open} content={modalContent} onClose={closeModal} />
      )}
    </ModalProviderContext.Provider>
  );
}

export const useModal = () => {
  const context = useContext(ModalProviderContext);

  if (context === undefined)
    throw new Error('useModal must be used within a ModalProvider');

  return context;
};

interface ModalProps {
  isOpen: boolean;
  content: React.ReactNode;
  onClose: () => void;
}

const Modal: React.FC<ModalProps> = ({ isOpen, content, onClose }) => {
  if (!isOpen) return null; // Don't render if the modal is not open

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {content}
    </Dialog>
  );
};
